/**
 * Custom hooks for Regional Targeting functionality
 */

import { useState, useEffect, useCallback } from 'react';
import { regionalApiService } from '../services/regionalService';
import { 
  State, 
  TargetScope, 
  RegionalPriority, 
  StateLanguage,
  RegionalTargetingPayload,
  RegionalSelection,
  UseRegionalSelectionReturn
} from '../types/regionalTypes';

// Re-export the interface for easier importing
export type { UseRegionalSelectionReturn } from '../types/regionalTypes';

// Hook for fetching states
export interface UseStatesReturn {
  states: State[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useStates = (): UseStatesReturn => {
  const [states, setStates] = useState<State[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStates = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await regionalApiService.getStates();
      console.log('States fetched successfully:', data.length, 'states');
      setStates(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch states';
      console.error('States API error:', err);
      setError(errorMessage);
      setStates([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStates();
  }, [fetchStates]);

  return {
    states,
    isLoading,
    error,
    refetch: fetchStates,
  };
};

// Hook for fetching target scopes
export interface UseTargetScopesReturn {
  targetScopes: TargetScope[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useTargetScopes = (): UseTargetScopesReturn => {
  const [targetScopes, setTargetScopes] = useState<TargetScope[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTargetScopes = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await regionalApiService.getTargetScopes();
      setTargetScopes(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch target scopes';
      setError(errorMessage);
      setTargetScopes([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTargetScopes();
  }, [fetchTargetScopes]);

  return {
    targetScopes,
    isLoading,
    error,
    refetch: fetchTargetScopes,
  };
};

// Hook for fetching regional priorities
export interface UseRegionalPrioritiesReturn {
  regionalPriorities: RegionalPriority[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useRegionalPriorities = (): UseRegionalPrioritiesReturn => {
  const [regionalPriorities, setRegionalPriorities] = useState<RegionalPriority[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRegionalPriorities = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await regionalApiService.getRegionalPriorities();
      setRegionalPriorities(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch regional priorities';
      setError(errorMessage);
      setRegionalPriorities([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRegionalPriorities();
  }, [fetchRegionalPriorities]);

  return {
    regionalPriorities,
    isLoading,
    error,
    refetch: fetchRegionalPriorities,
  };
};

// Hook for fetching state languages
export interface UseStateLanguagesReturn {
  languages: StateLanguage[];
  isLoading: boolean;
  error: string | null;
  refetch: (stateId: number) => Promise<void>;
}

export const useStateLanguages = (): UseStateLanguagesReturn => {
  const [languages, setLanguages] = useState<StateLanguage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStateLanguages = useCallback(async (stateId: number) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await regionalApiService.getStateLanguages(stateId);
      setLanguages(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch state languages';
      setError(errorMessage);
      setLanguages([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    languages,
    isLoading,
    error,
    refetch: fetchStateLanguages,
  };
};

// Hook for regional selection management (similar to category selection)
export const useRegionalSelection = (
  initialSelection: RegionalSelection = { 
    selectedStates: [], 
    primaryStateId: null, 
    targetScopeId: null, 
    regionalPriorityId: null, 
    languageId: null 
  }
): UseRegionalSelectionReturn => {
  const [selection, setSelection] = useState<RegionalSelection>(initialSelection);

  const toggleState = useCallback((stateId: number) => {
    setSelection(prev => {
      const isCurrentlySelected = prev.selectedStates.includes(stateId);
      
      if (isCurrentlySelected) {
        // Remove state
        const newSelectedStates = prev.selectedStates.filter(id => id !== stateId);
        return {
          ...prev,
          selectedStates: newSelectedStates,
          primaryStateId: prev.primaryStateId === stateId 
            ? (newSelectedStates.length > 0 ? newSelectedStates[0] : null)
            : prev.primaryStateId
        };
      } else {
        // Add state
        const newSelectedStates = [...prev.selectedStates, stateId];
        return {
          ...prev,
          selectedStates: newSelectedStates,
          primaryStateId: prev.primaryStateId || stateId // Set as primary if no primary exists
        };
      }
    });
  }, []);

  const setPrimaryState = useCallback((stateId: number) => {
    setSelection(prev => {
      // Ensure the state is selected before setting as primary
      if (!prev.selectedStates.includes(stateId)) {
        return {
          ...prev,
          selectedStates: [...prev.selectedStates, stateId],
          primaryStateId: stateId
        };
      }
      
      return {
        ...prev,
        primaryStateId: stateId
      };
    });
  }, []);

  const setTargetScope = useCallback((scopeId: number) => {
    setSelection(prev => ({
      ...prev,
      targetScopeId: scopeId
    }));
  }, []);

  const setRegionalPriority = useCallback((priorityId: number) => {
    setSelection(prev => ({
      ...prev,
      regionalPriorityId: priorityId
    }));
  }, []);

  const setLanguage = useCallback((languageId: number) => {
    setSelection(prev => ({
      ...prev,
      languageId
    }));
  }, []);

  const clearSelection = useCallback(() => {
    setSelection({ 
      selectedStates: [], 
      primaryStateId: null, 
      targetScopeId: null, 
      regionalPriorityId: null, 
      languageId: null 
    });
  }, []);

  const isStateSelected = useCallback((stateId: number) => {
    return selection.selectedStates.includes(stateId);
  }, [selection.selectedStates]);

  const isPrimaryState = useCallback((stateId: number) => {
    return selection.primaryStateId === stateId;
  }, [selection.primaryStateId]);

  const getSelectedStates = useCallback((allStates: State[]) => {
    return allStates.filter(state => selection.selectedStates.includes(state.stateId));
  }, [selection.selectedStates]);

  const getPrimaryState = useCallback((allStates: State[]) => {
    if (!selection.primaryStateId) return null;
    return allStates.find(state => state.stateId === selection.primaryStateId) || null;
  }, [selection.primaryStateId]);

  const validateSelection = useCallback(() => {
    return selection.selectedStates.length > 0 && 
           selection.targetScopeId !== null && 
           selection.regionalPriorityId !== null;
  }, [selection]);

  const getRegionalPayload = useCallback((
    allStates: State[], 
    allScopes: TargetScope[], 
    allPriorities: RegionalPriority[], 
    allLanguages: StateLanguage[]
  ): RegionalTargetingPayload | null => {
    if (!validateSelection()) return null;

    const selectedStates = getSelectedStates(allStates);
    const primaryState = getPrimaryState(allStates);
    const secondaryStates = selectedStates.filter(state => state.stateId !== selection.primaryStateId);
    const targetScope = allScopes.find(scope => scope.scopeId === selection.targetScopeId);
    const priority = allPriorities.find(p => p.priorityId === selection.regionalPriorityId);
    const language = allLanguages.find(lang => lang.languageId === selection.languageId);

    return {
      targetStateIds: selection.selectedStates,
      targetStateNames: selectedStates.map(state => state.stateName),
      targetScopeId: selection.targetScopeId!,
      targetScopeCode: targetScope?.scopeCode,
      regionalPriorityId: selection.regionalPriorityId!,
      regionalPriorityCode: priority?.priorityCode,
      primaryStateId: selection.primaryStateId || undefined,
      secondaryStateIds: secondaryStates.length > 0 ? secondaryStates.map(state => state.stateId) : undefined,
      languageId: selection.languageId || undefined,
      languageCode: language?.languageCode
    };
  }, [selection, validateSelection, getSelectedStates, getPrimaryState]);

  return {
    selection,
    toggleState,
    setPrimaryState,
    setTargetScope,
    setRegionalPriority,
    setLanguage,
    clearSelection,
    isStateSelected,
    isPrimaryState,
    getSelectedStates,
    getPrimaryState,
    validateSelection,
    getRegionalPayload,
  };
};

export default {
  useStates,
  useTargetScopes,
  useRegionalPriorities,
  useStateLanguages,
  useRegionalSelection,
};
